import { USER_WORKER_URL, isSfwSite } from '../config.js';
import { storage } from './storage.js';

const DEFAULT_USAGE_BUCKET = Object.freeze({
  used: 0,
  limit: 0,
});

const USAGE_BUCKET_KEYS = Object.freeze([
  'chat_credits',
  'audio_messages',
  'sprite_credits',
  'character_creation',
  'scenario_creation',
]);

const GROUP_CONVERSATION_TIERS = new Set(['single', 'harem_protagonist']);

export const tierHasGroupConversations = (tier) =>
  GROUP_CONVERSATION_TIERS.has(String(tier || '').trim().toLowerCase());

export const perkTierForUser = (userData) => {
  const tier = userData?.current_tier || 'free';
  if (!isSfwSite() && userData?.crypto_access === true) return 'harem_protagonist';
  return tier;
};

export const rememberCryptoAccess = (access) => {
  const current = storage.get('userData') || {};
  const next = access === true;
  if (current.crypto_access === next) return current;
  const updated = { ...current, crypto_access: next };
  storage.set('userData', updated);
  return updated;
};

const toNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeUsageBucket = (bucket) => {
  if (!bucket || typeof bucket !== 'object') return { ...DEFAULT_USAGE_BUCKET };
  return {
    used: toNumber(bucket.used),
    limit: Math.max(0, toNumber(bucket.limit)),
  };
};

export const getRemainingQuota = (bucket) => {
  const normalized = normalizeUsageBucket(bucket);
  return Math.max(0, normalized.limit - normalized.used);
};

export const parseMembershipUsagePayload = (payload) => {
  const source = payload && typeof payload === 'object' ? payload : {};
  const usageSource = source.usage && typeof source.usage === 'object' ? source.usage : {};

  const usage = USAGE_BUCKET_KEYS.reduce((accumulator, key) => {
    accumulator[key] = normalizeUsageBucket(usageSource[key]);
    return accumulator;
  }, {});

  const currentTier = typeof source.current_tier === 'string' && source.current_tier.trim()
    ? source.current_tier
    : 'free';

  const remaining = {
    chat_credits: getRemainingQuota(usage.chat_credits),
    audio_messages: getRemainingQuota(usage.audio_messages),
    sprite_credits: getRemainingQuota(usage.sprite_credits),
    character_creation: getRemainingQuota(usage.character_creation),
    scenario_creation: getRemainingQuota(usage.scenario_creation),
  };

  const usageResetsAt = typeof source.usage_resets_at === 'string' && source.usage_resets_at.trim()
    ? source.usage_resets_at
    : null;
  const usageWindowStart = typeof source.usage_window_start === 'string' && source.usage_window_start.trim()
    ? source.usage_window_start
    : null;
  const billingCycle = typeof source.billing_cycle === 'string' && source.billing_cycle.trim()
    ? source.billing_cycle.trim().toLowerCase()
    : null;
  const subscriptionRenewsAt = typeof source.subscription_renews_at === 'string' && source.subscription_renews_at.trim()
    ? source.subscription_renews_at
    : null;

  const cryptoBalance = Number(source.crypto_balance);
  const cryptoAccess = source.crypto_access === true || (Number.isFinite(cryptoBalance) && cryptoBalance > 0);

  return {
    current_tier: currentTier,
    crypto_balance: Number.isFinite(cryptoBalance) ? cryptoBalance : 0,
    crypto_access: cryptoAccess,
    created_at: source.created_at || null,
    usage_resets_at: usageResetsAt,
    usage_window_start: usageWindowStart,
    billing_cycle: billingCycle,
    subscription_renews_at: subscriptionRenewsAt,
    buzz: toNumber(source.buzz),
    seconds_until_next_buzz: Math.max(0, toNumber(source.seconds_until_next_buzz, 3600)),
    usage,
    remaining,
    authorization: {
      character_creation: usage.character_creation.limit > 0,
      audio_credits: usage.audio_messages.limit > 0,
      group_conversations: tierHasGroupConversations(currentTier),
    },
  };
};

export const parseMembershipUsageResponse = (responseBody) => {
  if (!responseBody || responseBody.success !== true || !responseBody.data) return null;
  return parseMembershipUsagePayload(responseBody.data);
};

const normalizeHistoryItem = (item) => {
  const source = item && typeof item === 'object' ? item : {};

  return {
    created_at: typeof source.created_at === 'string' ? source.created_at : null,
    category: typeof source.category === 'string' ? source.category : '',
    usage_type: typeof source.usage_type === 'string' ? source.usage_type : '',
    name: typeof source.name === 'string' ? source.name : '',
    credits_used: toNumber(source.credits_used),
  };
};

export const parseMembershipUsageHistoryResponse = (responseBody) => {
  if (!responseBody || responseBody.success !== true || !responseBody.data || typeof responseBody.data !== 'object') {
    return null;
  }

  const source = responseBody.data;
  const items = Array.isArray(source.items) ? source.items.map(normalizeHistoryItem) : [];
  const availableUsageTypes = Array.isArray(source.available_usage_types)
    ? source.available_usage_types.filter((value) => typeof value === 'string' && value.trim())
    : [];
  const totalCount = Math.max(0, toNumber(source.total_count));
  const limit = Math.max(1, toNumber(source.limit, 100));
  const offset = Math.max(0, toNumber(source.offset));

  return {
    items,
    available_usage_types: availableUsageTypes,
    total_count: totalCount,
    limit,
    offset,
    has_next: Boolean(source.has_next),
    has_previous: Boolean(source.has_previous),
  };
};

export const fetchMembershipUsageHistory = async ({
  token,
  limit = 100,
  offset = 0,
  usage_type = null,
  date_from = null,
  date_to = null,
}) => {
  if (!token) throw new Error('No authentication token');

  const payload = {
    limit: Math.min(Math.max(toNumber(limit, 100), 1), 100),
    offset: Math.max(toNumber(offset), 0),
  };

  if (typeof usage_type === 'string' && usage_type.trim()) payload.usage_type = usage_type.trim();
  if (typeof date_from === 'string' && date_from.trim()) payload.date_from = date_from.trim();
  if (typeof date_to === 'string' && date_to.trim()) payload.date_to = date_to.trim();

  const response = await fetch(`${USER_WORKER_URL}/membership-usage/history`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  let responseBody = null;
  try {
    responseBody = await response.json();
  } catch {
    responseBody = null;
  }

  if (!response.ok) {
    const message = responseBody?.error || `HTTP ${response.status}`;
    throw new Error(message);
  }

  return parseMembershipUsageHistoryResponse(responseBody);
};
